/**
 * Generic Repository Pattern for Redux
 *
 * Reduces boilerplate by automatically generating actions, reducers,
 * thunks, selectors, and custom hooks for common CRUD operations.
 *
 * SIMPLE USAGE (Just provide endpoint - everything else is automatic!):
 * const repo = createRepository({
 *   endpoint: 'admin/enterprise-subscriptions',
 * });
 * // Auto-generates:
 * // - name: 'adminEnterpriseSubscriptions'
 * // - statePath: 'adminEnterpriseSubscriptions'
 * // - All CRUD API methods
 * // - Hook: useAdminEnterpriseSubscriptions()
 *
 * WITH CUSTOM OPTIONS:
 * const repo = createRepository({
 *   endpoint: 'admin/enterprise-subscriptions',
 *   name: 'adminSubscriptions',        // Custom name (optional)
 *   statePath: 'adminSubscriptions',   // Custom Redux path (optional)
 *   getItemId: (item) => item.user_id, // Custom ID extractor (default: item.id)
 *   customMethods: {                    // Add custom API methods (optional)
 *     expiring: ({ authToken, dispatch }) => get({ ... })
 *   }
 * });
 *
 * MANUAL API (Full control - original way):
 * const repo = createRepository({
 *   name: 'adminSubscriptions',
 *   statePath: 'adminSubscriptions',
 *   api: { fetchAll, fetchOne, create, update, delete },
 *   getItemId: (item) => item.id,
 * });
 *
 * In component:
 * const { items, isLoading, actions } = useAdminSubscriptions({ autoFetch: true });
 */

import { useDispatch, useSelector } from 'react-redux';
import { useMemo, useCallback, useEffect } from 'react';
import { createAPI } from './createAPI';

// ============================================
// Auto-generate Custom Method State & Actions
// ============================================
const autoGenerateCustomMethodConfig = (name, customMethods) => {
	const customActionTypes = {};
	const customInitialState = {};
	const customReducers = {};

	Object.keys(customMethods).forEach((methodName) => {
		// Convert camelCase to UPPER_SNAKE_CASE
		// e.g., getStatistics -> GET_STATISTICS
		const upperMethodName = methodName
			.replace(/([A-Z])/g, '_$1')
			.toUpperCase()
			.replace(/^_/, '');

		// Generate action types
		const requestType = `${name}/${upperMethodName}_REQUEST`;
		const successType = `${name}/${upperMethodName}_SUCCESS`;
		const errorType = `${name}/${upperMethodName}_ERROR`;

		customActionTypes[`${upperMethodName}_REQUEST`] = requestType;
		customActionTypes[`${upperMethodName}_SUCCESS`] = successType;
		customActionTypes[`${upperMethodName}_ERROR`] = errorType;

		// Generate state fields
		const stateKey = methodName.replace(/^(get|fetch)/, '');
		const stateKeyLower = stateKey.charAt(0).toLowerCase() + stateKey.slice(1);

		// Determine default value based on method name
		let defaultValue = null;
		if (methodName.includes('Options') || methodName.includes('List')) {
			defaultValue = [];
		} else if (methodName.includes('Count') || methodName.includes('Total')) {
			defaultValue = 0;
		} else if (methodName.includes('Is') || methodName.includes('Has')) {
			defaultValue = false;
		}

		// Create separate state fields for data and pagination
		customInitialState[stateKeyLower] = defaultValue;
		customInitialState[`${stateKeyLower}Pagination`] = null; // Separate pagination state
		customInitialState[`isLoading${stateKey}`] = false;
		customInitialState[`${stateKeyLower}Error`] = null;

		// Generate reducers
		customReducers[requestType] = (state) => ({
			...state,
			[`isLoading${stateKey}`]: true,
			[`${stateKeyLower}Error`]: null,
		});

		// Store data and pagination separately
		customReducers[successType] = (state, action) => {
			const payload = action.payload;

			// Check if response has pagination structure (data + meta)
			if (
				payload &&
				typeof payload === 'object' &&
				payload.data !== undefined
			) {
				// Paginated response - store data and pagination separately
				let statePagination = null;
				if (payload?.meta) {
					statePagination = payload?.meta;
				} else if (payload?.total && payload?.per_page) {
					statePagination = {
						total: payload.total,
						perPage: payload.per_page,
						currentPage: payload.current_page,
						lastPage: payload.last_page,
						from: payload.from,
						to: payload.to,
					};
				}

				return {
					...state,
					[`isLoading${stateKey}`]: false,
					[stateKeyLower]: payload.data,
					[`${stateKeyLower}Pagination`]: statePagination,
					[`${stateKeyLower}Error`]: null,
				};
			} else {
				// Non-paginated response - store as-is
				return {
					...state,
					[`isLoading${stateKey}`]: false,
					[stateKeyLower]: payload,
					[`${stateKeyLower}Pagination`]: null,
					[`${stateKeyLower}Error`]: null,
				};
			}
		};

		customReducers[errorType] = (state, action) => ({
			...state,
			[`isLoading${stateKey}`]: false,
			[`${stateKeyLower}Error`]: action.payload,
		});
	});

	return { customActionTypes, customInitialState, customReducers };
};

// ============================================
// Action Type Generator
// ============================================
const createActionTypes = (name, customActionTypes = {}) => ({
	FETCH_REQUEST: `${name}/FETCH_REQUEST`,
	FETCH_SUCCESS: `${name}/FETCH_SUCCESS`,
	FETCH_ERROR: `${name}/FETCH_ERROR`,

	FETCH_ONE_REQUEST: `${name}/FETCH_ONE_REQUEST`,
	FETCH_ONE_SUCCESS: `${name}/FETCH_ONE_SUCCESS`,
	FETCH_ONE_ERROR: `${name}/FETCH_ONE_ERROR`,

	CREATE_REQUEST: `${name}/CREATE_REQUEST`,
	CREATE_SUCCESS: `${name}/CREATE_SUCCESS`,
	CREATE_ERROR: `${name}/CREATE_ERROR`,

	UPDATE_REQUEST: `${name}/UPDATE_REQUEST`,
	UPDATE_SUCCESS: `${name}/UPDATE_SUCCESS`,
	UPDATE_ERROR: `${name}/UPDATE_ERROR`,

	DELETE_REQUEST: `${name}/DELETE_REQUEST`,
	DELETE_SUCCESS: `${name}/DELETE_SUCCESS`,
	DELETE_ERROR: `${name}/DELETE_ERROR`,

	RESET: `${name}/RESET`,
	CLEAR_ERRORS: `${name}/CLEAR_ERRORS`,

	...customActionTypes,
});

// ============================================
// Initial State Generator
// ============================================
const createInitialState = (customInitialState = {}) => ({
	items: [],
	entities: {}, // Normalized by ID for quick lookup
	selectedItem: null,

	// Pagination metadata (Laravel format)
	pagination: {
		currentPage: 1,
		lastPage: 1,
		perPage: 15,
		total: 0,
		from: null,
		to: null,
	},

	// Loading states
	isLoading: false,
	isCreating: false,
	isUpdating: false,
	isDeleting: false,

	// Error states
	error: null,
	createError: null,
	updateError: null,
	deleteError: null,

	...customInitialState,
});

// ============================================
// Action Creators Generator
// ============================================
const createActionCreators = (types) => ({
	// Fetch All
	fetchRequest: () => ({ type: types.FETCH_REQUEST }),
	fetchSuccess: (items) => ({ type: types.FETCH_SUCCESS, payload: items }),
	fetchError: (error) => ({ type: types.FETCH_ERROR, payload: error }),

	// Fetch One
	fetchOneRequest: () => ({ type: types.FETCH_ONE_REQUEST }),
	fetchOneSuccess: (item) => ({ type: types.FETCH_ONE_SUCCESS, payload: item }),
	fetchOneError: (error) => ({ type: types.FETCH_ONE_ERROR, payload: error }),

	// Create
	createRequest: () => ({ type: types.CREATE_REQUEST }),
	createSuccess: (item) => ({ type: types.CREATE_SUCCESS, payload: item }),
	createError: (error) => ({ type: types.CREATE_ERROR, payload: error }),

	// Update
	updateRequest: () => ({ type: types.UPDATE_REQUEST }),
	updateSuccess: (item) => ({ type: types.UPDATE_SUCCESS, payload: item }),
	updateError: (error) => ({ type: types.UPDATE_ERROR, payload: error }),

	// Delete
	deleteRequest: () => ({ type: types.DELETE_REQUEST }),
	deleteSuccess: (id) => ({ type: types.DELETE_SUCCESS, payload: id }),
	deleteError: (error) => ({ type: types.DELETE_ERROR, payload: error }),

	// Utility
	reset: () => ({ type: types.RESET }),
	clearErrors: () => ({ type: types.CLEAR_ERRORS }),
});

// ============================================
// Reducer Generator
// ============================================
const createReducer = (types, initialState, getItemId, customReducers = {}) => {
	return (state = initialState, action) => {
		// Check custom reducers first
		if (customReducers[action.type]) {
			return customReducers[action.type](state, action);
		}
		switch (action.type) {
			// Fetch All
			case types.FETCH_REQUEST:
				return { ...state, isLoading: true, error: null };

			case types.FETCH_SUCCESS: {
				const payload = action.payload;

				// Handle Laravel pagination structure
				const isPaginated = payload.data && Array.isArray(payload.data);
				const items = isPaginated
					? payload.data
					: Array.isArray(payload)
						? payload
						: [];

				const entities = items.reduce((acc, item) => {
					acc[getItemId(item)] = item;
					return acc;
				}, {});

				// Extract pagination metadata if present
				const pagination = isPaginated
					? {
							currentPage: payload.current_page || 1,
							lastPage: payload.last_page || 1,
							perPage: payload.per_page || 15,
							total: payload.total || items.length,
							from: payload.from || null,
							to: payload.to || null,
						}
					: {
							...state.pagination,
							total: items.length,
						};

				return {
					...state,
					isLoading: false,
					items,
					entities,
					pagination,
					error: null,
				};
			}

			case types.FETCH_ERROR:
				return { ...state, isLoading: false, error: action.payload };

			// Fetch One
			case types.FETCH_ONE_REQUEST:
				return { ...state, isLoading: true, error: null };

			case types.FETCH_ONE_SUCCESS: {
				const item = action.payload;
				const itemId = getItemId(item);
				return {
					...state,
					isLoading: false,
					entities: { ...state.entities, [itemId]: item },
					selectedItem: item,
					error: null,
				};
			}

			case types.FETCH_ONE_ERROR:
				return { ...state, isLoading: false, error: action.payload };

			// Create
			case types.CREATE_REQUEST:
				return { ...state, isCreating: true, createError: null };

			case types.CREATE_SUCCESS: {
				const item = action.payload;
				const itemId = getItemId(item);
				return {
					...state,
					isCreating: false,
					items: [...state.items, item],
					entities: { ...state.entities, [itemId]: item },
					createError: null,
				};
			}

			case types.CREATE_ERROR:
				return { ...state, isCreating: false, createError: action.payload };

			// Update
			case types.UPDATE_REQUEST:
				return { ...state, isUpdating: true, updateError: null };

			case types.UPDATE_SUCCESS: {
				const item = action.payload;
				const itemId = getItemId(item);
				return {
					...state,
					isUpdating: false,
					items: state.items.map((i) => (getItemId(i) === itemId ? item : i)),
					entities: { ...state.entities, [itemId]: item },
					selectedItem:
						state.selectedItem && getItemId(state.selectedItem) === itemId
							? item
							: state.selectedItem,
					updateError: null,
				};
			}

			case types.UPDATE_ERROR:
				return { ...state, isUpdating: false, updateError: action.payload };

			// Delete
			case types.DELETE_REQUEST:
				return { ...state, isDeleting: true, deleteError: null };

			case types.DELETE_SUCCESS: {
				const itemId = action.payload;
				const newEntities = { ...state.entities };
				delete newEntities[itemId];
				return {
					...state,
					isDeleting: false,
					items: state.items.filter((i) => getItemId(i) !== itemId),
					entities: newEntities,
					selectedItem:
						state.selectedItem && getItemId(state.selectedItem) === itemId
							? null
							: state.selectedItem,
					deleteError: null,
				};
			}

			case types.DELETE_ERROR:
				return { ...state, isDeleting: false, deleteError: action.payload };

			// Reset
			case types.RESET:
				return createInitialState();

			// Clear Errors
			case types.CLEAR_ERRORS:
				return {
					...state,
					error: null,
					createError: null,
					updateError: null,
					deleteError: null,
				};

			default:
				return state;
		}
	};
};

// ============================================
// Thunk Actions Generator
// ============================================
const createThunks = (name, types, actions, api, options = {}) => {
	const { getAuthToken = (getState) => getState().user.authToken } = options;

	const baseThunks = {
		fetchAll:
			(params = {}) =>
			(dispatch, getState) => {
				dispatch(actions.fetchRequest());
				const authToken = getAuthToken(getState);

				return api
					.fetchAll({ authToken, dispatch, ...params })
					.then((response) => {
						const data = response.data || response;
						if (response?.current_page) {
							dispatch(actions.fetchSuccess(response));
						} else {
							dispatch(actions.fetchSuccess(data));
						}
						return response;
					})
					.catch((error) => {
						const errorMsg = error.message || `Failed to fetch ${name}`;
						dispatch(actions.fetchError(errorMsg));
						throw error;
					});
			},

		fetchPaginated:
			(page = 1, perPage = 15, params = {}) =>
			(dispatch, getState) => {
				dispatch(actions.fetchRequest());
				const authToken = getAuthToken(getState);

				return api
					.fetchAll({ authToken, dispatch, page, perPage, ...params })
					.then((response) => {
						const data = response.data || response;
						if (response?.current_page) {
							dispatch(actions.fetchSuccess(response));
						} else {
							dispatch(actions.fetchSuccess(data));
						}
						return response;
					})
					.catch((error) => {
						const errorMsg = error.message || `Failed to fetch ${name}`;
						dispatch(actions.fetchError(errorMsg));
						throw error;
					});
			},

		fetchOne:
			(id, params = {}) =>
			(dispatch, getState) => {
				dispatch(actions.fetchOneRequest());
				const authToken = getAuthToken(getState);

				return api
					.fetchOne({ id, authToken, dispatch, ...params })
					.then((response) => {
						const data = response.data || response;
						dispatch(actions.fetchOneSuccess(data));
						return response;
					})
					.catch((error) => {
						const errorMsg = error.message || `Failed to fetch ${name}`;
						dispatch(actions.fetchOneError(errorMsg));
						throw error;
					});
			},

		create:
			(data, params = {}) =>
			(dispatch, getState) => {
				dispatch(actions.createRequest());
				const authToken = getAuthToken(getState);

				return api
					.create({ data, authToken, dispatch, ...params })
					.then((response) => {
						const result = response.data || response;
						dispatch(actions.createSuccess(result));
						return response;
					})
					.catch((error) => {
						const errorMsg = error.message || `Failed to create ${name}`;
						dispatch(actions.createError(errorMsg));
						throw error;
					});
			},

		update:
			(id, data, params = {}) =>
			(dispatch, getState) => {
				dispatch(actions.updateRequest());
				const authToken = getAuthToken(getState);

				return api
					.update({ id, data, authToken, dispatch, ...params })
					.then((response) => {
						const result = response.data || response;
						dispatch(actions.updateSuccess(result));
						return response;
					})
					.catch((error) => {
						const errorMsg = error.message || `Failed to update ${name}`;
						dispatch(actions.updateError(errorMsg));
						throw error;
					});
			},

		delete:
			(id, params = {}) =>
			(dispatch, getState) => {
				dispatch(actions.deleteRequest());
				const authToken = getAuthToken(getState);

				return api
					.delete({ id, authToken, dispatch, ...params })
					.then((response) => {
						dispatch(actions.deleteSuccess(id));
						return response;
					})
					.catch((error) => {
						const errorMsg = error.message || `Failed to delete ${name}`;
						dispatch(actions.deleteError(errorMsg));
						throw error;
					});
			},

		reset: () => (dispatch) => {
			dispatch(actions.reset());
		},

		clearErrors: () => (dispatch) => {
			dispatch(actions.clearErrors());
		},
	};

	// Auto-generate thunks for custom methods! 🚀
	// Each customMethod becomes a thunk automatically
	const customThunks = {};
	const standardMethods = [
		'fetchAll',
		'fetchOne',
		'create',
		'update',
		'delete',
	];

	Object.keys(api).forEach((methodName) => {
		// Skip standard CRUD methods (already handled above)
		if (standardMethods.includes(methodName)) return;

		// Convert methodName to action type format
		const upperMethodName = methodName
			.replace(/([A-Z])/g, '_$1')
			.toUpperCase()
			.replace(/^_/, '');

		const requestType = `${name}/${upperMethodName}_REQUEST`;
		const successType = `${name}/${upperMethodName}_SUCCESS`;
		const errorType = `${name}/${upperMethodName}_ERROR`;

		// Create thunk for custom method
		customThunks[methodName] =
			(...args) =>
			(dispatch, getState) => {
				const authToken = getAuthToken(getState);
				// Support multiple argument patterns
				const params =
					typeof args[0] === 'object' && args[0] !== null ? args[0] : {};

				// Dispatch REQUEST action
				dispatch({ type: requestType });
				// Call the custom API method with authToken and dispatch
				return api[methodName]({ authToken, dispatch, ...params })
					.then((response) => {
						// Dispatch SUCCESS action with data
						dispatch({
							type: successType,
							payload:
								response?.per_page && response?.total && response?.current_page
									? response
									: response.data || response,
						});
						return response;
					})
					.catch((error) => {
						// Dispatch ERROR action
						const errorMsg =
							error.response?.data?.message ||
							error.response?.data?.error ||
							error.message ||
							`Failed to ${methodName}`;
						dispatch({
							type: errorType,
							payload: errorMsg,
						});
						throw error;
					});
			};
	});

	return {
		...baseThunks,
		...customThunks, // All custom methods are now available as thunks! ✨
	};
};

// ============================================
// Selectors Generator
// ============================================
const createSelectors = (statePath) => ({
	// Basic selectors (with safe defaults)
	selectAll: (state) => state[statePath]?.items || [],
	selectEntities: (state) => state[statePath]?.entities || {},
	selectById: (id) => (state) => state[statePath]?.entities?.[id] || null,
	selectSelectedItem: (state) => state[statePath]?.selectedItem || null,

	// Pagination selectors (with safe defaults)
	selectPagination: (state) => state[statePath]?.pagination || {},
	selectCurrentPage: (state) => state[statePath]?.pagination?.currentPage || 1,
	selectLastPage: (state) => state[statePath]?.pagination?.lastPage || 1,
	selectPerPage: (state) => state[statePath]?.pagination?.perPage || 15,
	selectTotal: (state) => state[statePath]?.pagination?.total || 0,
	selectHasNextPage: (state) => {
		const pagination = state[statePath]?.pagination;
		return pagination ? pagination.currentPage < pagination.lastPage : false;
	},
	selectHasPrevPage: (state) =>
		(state[statePath]?.pagination?.currentPage || 1) > 1,

	// Loading selectors (with safe defaults)
	selectIsLoading: (state) => state[statePath]?.isLoading || false,
	selectIsCreating: (state) => state[statePath]?.isCreating || false,
	selectIsUpdating: (state) => state[statePath]?.isUpdating || false,
	selectIsDeleting: (state) => state[statePath]?.isDeleting || false,
	selectIsPerformingAction: (state) => {
		const s = state[statePath];
		return s
			? s.isLoading || s.isCreating || s.isUpdating || s.isDeleting
			: false;
	},

	// Error selectors (with safe defaults)
	selectError: (state) => state[statePath]?.error || null,
	selectCreateError: (state) => state[statePath]?.createError || null,
	selectUpdateError: (state) => state[statePath]?.updateError || null,
	selectDeleteError: (state) => state[statePath]?.deleteError || null,
	selectAnyError: (state) => {
		const s = state[statePath];
		return s
			? s.error || s.createError || s.updateError || s.deleteError
			: null;
	},

	// Computed selectors (with safe defaults)
	selectCount: (state) => state[statePath]?.items?.length || 0,
	selectIsEmpty: (state) => (state[statePath]?.items?.length || 0) === 0,
	selectHasData: (state) => (state[statePath]?.items?.length || 0) > 0,

	// Filter selectors (with safe defaults)
	selectWhere: (predicate) => (state) =>
		state[statePath]?.items?.filter(predicate) || [],

	selectFirst: (state) => state[statePath]?.items?.[0] || null,
	selectLast: (state) => {
		const items = state[statePath]?.items;
		return items ? items[items.length - 1] || null : null;
	},
});

// ============================================
// Custom Hook Generator
// ============================================
const createHook = (statePath, thunks, selectors, customInitialState = {}) => {
	return (options = {}) => {
		const dispatch = useDispatch();
		const { autoFetch = false, fetchParams = {}, selectById = null } = options;

		const repoState = useSelector((state) => state[statePath]);
		// Select state
		const items = useSelector(selectors.selectAll);
		const entities = useSelector(selectors.selectEntities);
		const selectedItem = useSelector(selectors.selectSelectedItem);

		// Pagination state
		const pagination = useSelector(selectors.selectPagination);
		const currentPage = useSelector(selectors.selectCurrentPage);
		const lastPage = useSelector(selectors.selectLastPage);
		const perPage = useSelector(selectors.selectPerPage);
		const total = useSelector(selectors.selectTotal);
		const hasNextPage = useSelector(selectors.selectHasNextPage);
		const hasPrevPage = useSelector(selectors.selectHasPrevPage);

		// Loading states
		const isLoading = useSelector(selectors.selectIsLoading);
		const isCreating = useSelector(selectors.selectIsCreating);
		const isUpdating = useSelector(selectors.selectIsUpdating);
		const isDeleting = useSelector(selectors.selectIsDeleting);
		const isPerformingAction = useSelector(selectors.selectIsPerformingAction);

		// Error states
		const error = useSelector(selectors.selectError);
		const createError = useSelector(selectors.selectCreateError);
		const updateError = useSelector(selectors.selectUpdateError);
		const deleteError = useSelector(selectors.selectDeleteError);
		const anyError = useSelector(selectors.selectAnyError);

		// Computed states
		const count = useSelector(selectors.selectCount);
		const isEmpty = useSelector(selectors.selectIsEmpty);
		const hasData = useSelector(selectors.selectHasData);

		// Select specific item by ID if provided
		const itemById = useSelector(
			selectById ? selectors.selectById(selectById) : () => null,
		);

		// Memoized bound actions (including custom methods!)
		const actions = useMemo(() => {
			const baseActions = {
				fetchAll: (params) => dispatch(thunks.fetchAll(params)),
				fetchPaginated: (page, perPage, params) =>
					dispatch(thunks.fetchPaginated(page, perPage, params)),
				fetchOne: (id, params) => dispatch(thunks.fetchOne(id, params)),
				create: (data, params) => dispatch(thunks.create(data, params)),
				update: (id, data, params) => dispatch(thunks.update(id, data, params)),
				delete: (id, params) => dispatch(thunks.delete(id, params)),
				reset: () => dispatch(thunks.reset()),
				clearErrors: () => dispatch(thunks.clearErrors()),
			};

			// Auto-bind custom thunks to actions 🎉
			const customActions = {};
			const standardMethods = [
				'fetchAll',
				'fetchPaginated',
				'fetchOne',
				'create',
				'update',
				'delete',
				'reset',
				'clearErrors',
			];

			Object.keys(thunks).forEach((thunkName) => {
				if (!standardMethods.includes(thunkName)) {
					// Bind custom thunk with support for multiple arguments
					customActions[thunkName] = (...args) =>
						dispatch(thunks[thunkName](...args));
				}
			});

			return { ...baseActions, ...customActions };
		}, [dispatch]);

		// Auto-fetch on mount if enabled
		useEffect(() => {
			if (autoFetch) {
				actions.fetchAll(fetchParams);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [autoFetch]);

		// Callback helpers
		const getById = useCallback((id) => entities[id] || null, [entities]);
		const findWhere = useCallback(
			(predicate) => items.filter(predicate),
			[items],
		);

		// Build custom state object
		const customState = {};
		Object.keys(customInitialState).forEach((key) => {
			customState[key] = repoState?.[key];
		});

		return {
			// Data
			items,
			entities,
			selectedItem,
			itemById,
			count,
			isEmpty,
			hasData,

			// Pagination
			pagination,
			currentPage,
			lastPage,
			perPage,
			total,
			hasNextPage,
			hasPrevPage,

			// Loading states
			isLoading,
			isCreating,
			isUpdating,
			isDeleting,
			isPerformingAction,

			// Errors
			error,
			createError,
			updateError,
			deleteError,
			anyError,

			// Actions
			actions,

			// Helpers
			getById,
			findWhere,

			// Initial state (for reference)
			...customState,
		};
	};
};

/**
 * Convert endpoint slug to camelCase name
 * Example: 'admin/enterprise-subscriptions' -> 'adminEnterpriseSubscriptions'
 */
const endpointToName = (endpoint) => {
	return endpoint
		.split('/')
		.map((part, index) => {
			// Convert kebab-case to camelCase
			const camelPart = part.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
			// Capitalize first letter of each segment except first
			if (index === 0) return camelPart;
			return camelPart.charAt(0).toUpperCase() + camelPart.slice(1);
		})
		.join('');
};

// ============================================
// Main Repository Factory
// ============================================
/**
 * Create a complete repository with Redux state management
 *
 * @param {Object} config
 * @param {string} config.endpoint - API endpoint slug (e.g., 'admin/subscriptions') - Auto-generates API
 * @param {string} config.name - Repository name (defaults to camelCase of endpoint)
 * @param {string} config.statePath - Redux state path (defaults to name)
 * @param {Object} config.api - Manual API object (if not using auto-generated)
 * @param {Function} config.getItemId - Extract ID from item (defaults to item => item.id)
 * @param {Object} config.customMethods - Custom API methods to add
 * @param {string} config.updateMethod - HTTP method for updates ('patch' | 'post')
 * @param {string} config.deleteMethod - HTTP method for delete ('delete' | 'post')
 * @param {boolean} config.pagination - Enable pagination (default: true)
 * @param {Object} config.options - Additional options (getAuthToken, etc.)
 */
export const createRepository = (config) => {
	const {
		endpoint,
		name: providedName,
		statePath: providedStatePath,
		api: providedApi,
		getItemId = (item) => item.id,
		customMethods = {},
		updateMethod = 'patch',
		deleteMethod = 'delete',
		pagination = true,
		options = {},
	} = config;

	// Smart defaults: auto-generate name from endpoint
	const name =
		providedName || (endpoint ? endpointToName(endpoint) : 'repository');
	const statePath = providedStatePath || name;

	const { customActionTypes, customInitialState, customReducers } =
		autoGenerateCustomMethodConfig(name, customMethods);

	// Auto-generate API if endpoint is provided, otherwise use provided API
	const api =
		providedApi ||
		(endpoint
			? createAPI(endpoint, {
					customMethods,
					updateMethod,
					deleteMethod,
					pagination,
				})
			: null);

	if (!api) {
		throw new Error(
			'createRepository requires either "endpoint" (for auto-generated API) or "api" (for manual API)',
		);
	}

	const types = createActionTypes(name, customActionTypes);
	const initialState = createInitialState(customInitialState);
	const actions = createActionCreators(types);
	const thunks = createThunks(name, types, actions, api, options);
	const reducer = createReducer(types, initialState, getItemId, customReducers);
	const selectors = createSelectors(statePath);
	const useHook = createHook(statePath, thunks, selectors, customInitialState);

	return {
		name,
		statePath,
		types,
		actions,
		thunks,
		reducer,
		initialState,
		selectors,
		useHook,
	};
};

export default createRepository;
